import { getWeeksBetween } from './dateHelpers';

/**
 * Generate a block rotation schedule for residents
 * NEW ALGORITHM: Process residents one at a time, assigning all rotations for each resident across all blocks
 * @param {Array} filteredResidents - Array of resident objects
 * @param {string} startDate - Start date in YYYY-MM-DD format
 * @param {string} endDate - End date in YYYY-MM-DD format
 * @param {string} blockPattern - Block pattern (e.g., "4-1" for 4 weeks major, 1 week minor)
 * @param {number} blockDuration - Total weeks per block (e.g., 5 for 4-1 pattern)
 * @param {Array} selectedRotations - Array of rotation names to assign
 * @param {Object} blockRotations - Object mapping rotation names to block configuration
 * @returns {Object} Schedule object with resident IDs as keys
 */
export const generateBlockSchedule = async (
  filteredResidents,
  startDate,
  endDate,
  blockPattern,
  blockDuration,
  selectedRotations,
  blockRotations
) => {
  console.log('[Generate Block Schedule] Starting...', { filteredResidentsCount: filteredResidents.length, startDate, endDate });
  
  // Get all weeks between start and end date
  console.log('[Generate Block Schedule] Getting weeks between dates...');
  const weeks = getWeeksBetween(startDate, endDate);
  const totalWeeks = weeks.length;
  console.log('[Generate Block Schedule] Total weeks:', totalWeeks);

  if (totalWeeks === 0) {
    console.error('[Generate Block Schedule] No valid weeks found');
    throw new Error("No valid weeks found between start and end dates");
  }

  // Parse block pattern (e.g., "4-1" -> major: 4, minor: 1)
  const [majorWeeks, minorWeeks] = blockPattern.split('-').map(Number);
  console.log('[Generate Block Schedule] Block pattern:', { blockPattern, majorWeeks, minorWeeks, blockDuration });
  
  if (majorWeeks + minorWeeks !== blockDuration) {
    console.error('[Generate Block Schedule] Pattern mismatch:', { majorWeeks, minorWeeks, blockDuration });
    throw new Error(`Block pattern ${blockPattern} does not match block duration ${blockDuration}`);
  }

  // Initialize schedule
  console.log('[Generate Block Schedule] Initializing schedule for', filteredResidents.length, 'residents');
  const newSchedule = {};
  filteredResidents.forEach((resident) => {
    newSchedule[resident.id] = {
      name: `${resident.firstName} ${resident.lastName}`,
      year: parseInt(resident.year),
      rotations: {},
    };
    weeks.forEach((week) => {
      newSchedule[resident.id].rotations[week.start] = null;
    });
  });
  console.log('[Generate Block Schedule] Schedule initialized');

  // Group weeks into blocks
  console.log('[Generate Block Schedule] Grouping weeks into blocks...');
  const blocks = [];
  for (let i = 0; i < weeks.length; i += blockDuration) {
    const blockWeeks = weeks.slice(i, i + blockDuration);
    if (blockWeeks.length > 0) {
      blocks.push({
        majorWeeks: blockWeeks.slice(0, majorWeeks),
        minorWeeks: blockWeeks.slice(majorWeeks, majorWeeks + minorWeeks),
        allWeeks: blockWeeks,
        index: blocks.length,
      });
    }
  }
  console.log('[Generate Block Schedule] Created', blocks.length, 'blocks');

  // Handle remaining weeks that don't form a complete block
  const remainingWeeks = weeks.slice(blocks.length * blockDuration);
  if (remainingWeeks.length > 0) {
    if (remainingWeeks.length <= majorWeeks) {
      blocks.push({
        majorWeeks: remainingWeeks,
        minorWeeks: [],
        allWeeks: remainingWeeks,
        isPartial: true,
        index: blocks.length,
      });
    } else if (remainingWeeks.length <= minorWeeks) {
      blocks.push({
        majorWeeks: [],
        minorWeeks: remainingWeeks,
        allWeeks: remainingWeeks,
        isPartial: true,
        index: blocks.length,
      });
    } else {
      blocks.push({
        majorWeeks: remainingWeeks.slice(0, majorWeeks),
        minorWeeks: remainingWeeks.slice(majorWeeks),
        allWeeks: remainingWeeks,
        isPartial: true,
        index: blocks.length,
      });
    }
  }

  const totalBlocks = blocks.length;

  // Separate rotations by column
  console.log('[Generate Block Schedule] Separating rotations by column...');
  const majorRotations = [];
  const minorRotations = [];
  
  selectedRotations.forEach(rotation => {
    const config = blockRotations[rotation];
    if (config && config.totalWeeks > 0) {
      if (config.column === 'major') {
        majorRotations.push({
          name: rotation,
          ...config,
        });
      } else {
        minorRotations.push({
          name: rotation,
          ...config,
        });
      }
    }
  });
  console.log('[Generate Block Schedule] Rotations separated:', { majorCount: majorRotations.length, minorCount: minorRotations.length });

  const allRotations = [...majorRotations, ...minorRotations];

  const getMaxResidentsPerBlock = (rotationConfig, columnType) => {
    if (columnType !== 'major' || !rotationConfig || !rotationConfig.totalWeeks) {
      // For minor rotations, don't enforce a per-block limit beyond other rules
      return Infinity;
    }
    const totalWeeksForRotation = rotationConfig.totalWeeks;
    if (totalWeeksForRotation <= 4) {
      return 1;
    }
    if (totalWeeksForRotation >= 6) {
      return Math.ceil(totalWeeksForRotation / 4);
    }
    // For 5-week rotations, allow up to 2 residents per block
    return Math.ceil(totalWeeksForRotation / 4);
  };

  const rotationBlockDistribution = {};
  const rotationBlocksCovered = {};
  allRotations.forEach(rot => {
    rotationBlockDistribution[rot.name] = Array(totalBlocks).fill(0);
    rotationBlocksCovered[rot.name] = 0;
  });

  const updateRotationBlockDistribution = (rotationName, blockIndex) => {
    if (!rotationBlockDistribution[rotationName]) {
      rotationBlockDistribution[rotationName] = Array(totalBlocks).fill(0);
      rotationBlocksCovered[rotationName] = 0;
    }
    if ((rotationBlockDistribution[rotationName][blockIndex] || 0) === 0) {
      rotationBlocksCovered[rotationName] = (rotationBlocksCovered[rotationName] || 0) + 1;
    }
    rotationBlockDistribution[rotationName][blockIndex] =
      (rotationBlockDistribution[rotationName][blockIndex] || 0) + 1;
  };

  // Track block coverage globally: { rotationName: { blockIndex: [residentIds] } }
  const globalBlockCoverage = {};
  [...majorRotations, ...minorRotations].forEach(rot => {
    globalBlockCoverage[rot.name] = {};
    for (let i = 0; i < totalBlocks; i++) {
      globalBlockCoverage[rot.name][i] = [];
    }
  });

  // Shuffle residents for random order
  const residentIds = Object.keys(newSchedule);
  const shuffledResidentIds = [...residentIds].sort(() => Math.random() - 0.5);
  console.log('[Generate Block Schedule] Processing', shuffledResidentIds.length, 'residents one at a time');

  const residentRotationNeeds = {};
  shuffledResidentIds.forEach(residentId => {
    residentRotationNeeds[residentId] = {};
    allRotations.forEach(rot => {
      residentRotationNeeds[residentId][rot.name] = rot.totalWeeks;
    });
  });

  const hasMoreResidentsThanBlocks = shuffledResidentIds.length > totalBlocks;

  /**
   * Assign all rotations for a single resident across all blocks
   * This is the core of the new algorithm
   */
  const assignResidentRotations = (residentId, rotationList, columnType, blockWeeksGetter) => {
    const isMajor = columnType === 'major';
    const targetBlockSize = isMajor ? majorWeeks : minorWeeks;
    
    // Track which rotations this resident still needs to complete
    const rotationNeeds = residentRotationNeeds[residentId] || {};
    rotationList.forEach(rot => {
      if (rotationNeeds[rot.name] === undefined) {
        rotationNeeds[rot.name] = rot.totalWeeks;
      }
    });

    // Track which blocks this resident has been assigned to for each rotation
    const residentBlockAssignments = {};
    rotationList.forEach(rot => {
      residentBlockAssignments[rot.name] = new Set();
    });

    // Helper to split an assignment into two chunks that still respect min/max block sizes
    const splitAssignment = (assignment) => {
      const minSize = Math.max(1, assignment.originalRotation?.minBlockSize || 1);
      const maxSize = Math.max(minSize, assignment.originalRotation?.maxBlockSize || assignment.weeks);
      if (assignment.weeks <= minSize) {
        return null;
      }

      for (let firstChunk = Math.min(maxSize, assignment.weeks - minSize); firstChunk >= minSize; firstChunk--) {
        const secondChunk = assignment.weeks - firstChunk;
        if (secondChunk >= minSize && secondChunk <= maxSize) {
          return [
            { ...assignment, weeks: firstChunk },
            { ...assignment, weeks: secondChunk },
          ];
        }
      }
      return null;
    };

    // Create a list of all rotation assignments needed for this resident
    const rotationAssignments = [];
    rotationList.forEach(rot => {
      let remainingWeeks = rotationNeeds[rot.name] || 0;
      if (remainingWeeks <= 0) {
        return;
      }

      const minBlockSize = Math.max(1, rot.minBlockSize || 1);
      const maxBlockSize = Math.max(minBlockSize, rot.maxBlockSize || remainingWeeks);

      while (remainingWeeks > 0) {
        let chunkSize = Math.min(maxBlockSize, remainingWeeks);

        // Ensure the remainder is either zero or large enough for another assignment
        while (chunkSize > minBlockSize && (remainingWeeks - chunkSize) > 0 && (remainingWeeks - chunkSize) < minBlockSize) {
          chunkSize--;
        }

        if (chunkSize < minBlockSize) {
          chunkSize = remainingWeeks; // fallback, we'll attempt to split later if needed
        }

        rotationAssignments.push({
          rotation: rot.name,
          weeks: chunkSize,
          originalRotation: rot,
        });

        remainingWeeks -= chunkSize;
      }
    });

    // Shuffle assignments for randomness
    rotationAssignments.sort(() => Math.random() - 0.5);

    // Try to assign each rotation assignment to a block
    const unassigned = [];
    const assignmentQueue = [...rotationAssignments];
    
    while (assignmentQueue.length > 0) {
      const assignment = assignmentQueue.shift();
      const { rotation, weeks, originalRotation } = assignment;
      
      // Find available blocks for this assignment
      const availableBlocks = blocks
        .map((block, blockIndex) => {
          const blockWeeks = blockWeeksGetter(block);
          const availableWeeks = blockWeeks.filter(week => {
            const weekKey = week.start;
            return newSchedule[residentId].rotations[weekKey] === null;
          });
          
          if (availableWeeks.length < weeks) {
            return null; // Not enough space
          }

          const otherResidentsInBlock = globalBlockCoverage[rotation]?.[blockIndex] || [];
          const totalResidentsHere = otherResidentsInBlock.length;
          let requiresException = false;
          
          // CONSTRAINT 1: For rotations ≤4 weeks, no duplicate per resident per block
          if (originalRotation.totalWeeks <= 4) {
            // Check if this resident already has this rotation in this block
            if (residentBlockAssignments[rotation].has(blockIndex)) {
              return null; // Already assigned to this block
            }
            
            if (totalResidentsHere > 0) {
              if (!hasMoreResidentsThanBlocks) {
                return null; // No overlap allowed
              }
              requiresException = true;
            }
          }
          
          // General resident-per-block limit
          const maxResidentsPerBlock = getMaxResidentsPerBlock(originalRotation, columnType);
          const exceedsLimit = totalResidentsHere >= maxResidentsPerBlock;

          const rotationUsageInBlock = rotationBlockDistribution[rotation]?.[blockIndex] || 0;
          if (exceedsLimit && maxResidentsPerBlock !== Infinity) {
            // Allow temporarily; we'll heavily penalize and only use if nothing else fits
          }

          let score = availableWeeks.length;

          if (rotationUsageInBlock === 0) {
            score += 200;
          } else {
            score -= rotationUsageInBlock * 150;
          }

          const blocksCovered = rotationBlocksCovered[rotation] || 0;
          const distributionRatio = totalBlocks === 0 ? 1 : blocksCovered / totalBlocks;
          if (distributionRatio < 0.5) {
            score += (0.5 - distributionRatio) * 200;
          } else {
            score -= (distributionRatio - 0.5) * 50;
          }

          if (requiresException) {
            score -= 5000;
          }

          if (exceedsLimit) {
            score -= 10000;
          }

          return {
            blockIndex,
            block,
            availableWeeks,
            score,
            exceedsLimit,
          };
        })
        .filter(b => b !== null);

      // For rotations ≤4 weeks, if other residents already have it, only allow if more residents than blocks
      if (originalRotation.totalWeeks <= 4 && availableBlocks.length > 0) {
        const blocksWithOtherResidents = availableBlocks.filter(b => {
          const otherResidents = globalBlockCoverage[rotation]?.[b.blockIndex] || [];
          return otherResidents.length > 0;
        });
        
        if (blocksWithOtherResidents.length === availableBlocks.length && !hasMoreResidentsThanBlocks) {
          // All available blocks have other residents, and we don't have more residents than blocks
          // Check if there are other rotation options
          const otherRotationOptions = rotationList.filter(r => 
            r.name !== rotation && 
            rotationNeeds[r.name] > 0
          );
          
          if (otherRotationOptions.length > 0) {
            // There are other options, so don't assign this one yet
            assignmentQueue.push(assignment);
            continue;
          }
        }
      }

      const preferredBlocks = availableBlocks.filter(block => !block.exceedsLimit);
      const candidateBlocks = preferredBlocks.length > 0 ? preferredBlocks : availableBlocks;

      // Sort available blocks by score
      candidateBlocks.sort((a, b) => b.score - a.score);

      if (candidateBlocks.length > 0) {
        const selectedBlock = candidateBlocks[0];
        const weeksToAssign = selectedBlock.availableWeeks.slice(0, weeks);
        
        // Assign the rotation to these weeks
        weeksToAssign.forEach(week => {
          newSchedule[residentId].rotations[week.start] = rotation;
        });

        // Update tracking
        if (!globalBlockCoverage[rotation]) {
          globalBlockCoverage[rotation] = {};
        }
        if (!globalBlockCoverage[rotation][selectedBlock.blockIndex]) {
          globalBlockCoverage[rotation][selectedBlock.blockIndex] = [];
        }
        if (!globalBlockCoverage[rotation][selectedBlock.blockIndex].includes(residentId)) {
          globalBlockCoverage[rotation][selectedBlock.blockIndex].push(residentId);
        }
        
        residentBlockAssignments[rotation].add(selectedBlock.blockIndex);
        rotationNeeds[rotation] = Math.max(0, (rotationNeeds[rotation] || 0) - weeks);
        updateRotationBlockDistribution(rotation, selectedBlock.blockIndex);
      } else {
        const splitCandidates = splitAssignment(assignment);
        if (splitCandidates) {
          assignmentQueue.push(...splitCandidates);
        } else {
          // No available block found, add to unassigned
          unassigned.push(assignment);
        }
      }
    }

    // Handle unassigned rotations - try to fit them anywhere possible
    unassigned.forEach(assignment => {
      const { rotation, weeks, originalRotation } = assignment;
      
      // Find any block with enough space
      for (let blockIndex = 0; blockIndex < blocks.length; blockIndex++) {
        const block = blocks[blockIndex];
        const blockWeeks = blockWeeksGetter(block);
        const availableWeeks = blockWeeks.filter(week => {
          const weekKey = week.start;
          return newSchedule[residentId].rotations[weekKey] === null;
        });
        
        if (availableWeeks.length >= weeks) {
          // Check constraints
          let canAssign = true;
          
          // CONSTRAINT 1: For rotations ≤4 weeks
          if (originalRotation.totalWeeks <= 4) {
            if (residentBlockAssignments[rotation].has(blockIndex)) {
              canAssign = false;
            }
          }
          
          const maxResidentsPerBlock = getMaxResidentsPerBlock(originalRotation, columnType);
          if (maxResidentsPerBlock !== Infinity && canAssign) {
            const currentResidentsInBlock = (globalBlockCoverage[rotation]?.[blockIndex] || []).length;
            if (currentResidentsInBlock >= maxResidentsPerBlock) {
              canAssign = false;
            }
          }
          
          if (canAssign) {
            const weeksToAssign = availableWeeks.slice(0, weeks);
            weeksToAssign.forEach(week => {
              newSchedule[residentId].rotations[week.start] = rotation;
            });

            // Update tracking
            if (!globalBlockCoverage[rotation]) {
              globalBlockCoverage[rotation] = {};
            }
            if (!globalBlockCoverage[rotation][blockIndex]) {
              globalBlockCoverage[rotation][blockIndex] = [];
            }
            if (!globalBlockCoverage[rotation][blockIndex].includes(residentId)) {
              globalBlockCoverage[rotation][blockIndex].push(residentId);
            }
            
            residentBlockAssignments[rotation].add(blockIndex);
            rotationNeeds[rotation] = Math.max(0, (rotationNeeds[rotation] || 0) - weeks);
            updateRotationBlockDistribution(rotation, blockIndex);
            break; // Assigned, move to next
          }
        }
      }
    });
  };

  // Process each resident one at a time, assigning all their rotations
  shuffledResidentIds.forEach((residentId, residentIndex) => {
    if (residentIndex % 5 === 0) {
      console.log('[Generate Block Schedule] Processing resident', residentIndex + 1, 'of', shuffledResidentIds.length);
    }

    // Assign major rotations first
    if (majorRotations.length > 0) {
      assignResidentRotations(
        residentId,
        majorRotations,
        'major',
        (block) => block.majorWeeks
      );
    }

    // Then assign minor rotations
    if (minorRotations.length > 0) {
      assignResidentRotations(
        residentId,
        minorRotations,
        'minor',
        (block) => block.minorWeeks
      );
    }
  });

  // Fill any remaining null slots with any available rotation
  console.log('[Generate Block Schedule] Filling remaining null slots...');
  shuffledResidentIds.forEach(residentId => {
    weeks.forEach(week => {
      if (newSchedule[residentId].rotations[week.start] === null) {
        // Find which block this week belongs to
        let blockIndex = -1;
        let columnType = null;
        let rotationList = [];
        
        for (let i = 0; i < blocks.length; i++) {
          const block = blocks[i];
          if (block.majorWeeks.some(w => w.start === week.start)) {
            blockIndex = i;
            columnType = 'major';
            rotationList = majorRotations;
            break;
          } else if (block.minorWeeks.some(w => w.start === week.start)) {
            blockIndex = i;
            columnType = 'minor';
            rotationList = minorRotations;
            break;
          }
        }
        
        if (blockIndex >= 0 && rotationList.length > 0) {
          const rotationNeeds = residentRotationNeeds[residentId] || {};
          let rotationToUse = rotationList.find(rot => (rotationNeeds[rot.name] || 0) > 0);
          
          if (!rotationToUse) {
            rotationToUse = rotationList[0];
          }

          newSchedule[residentId].rotations[week.start] = rotationToUse.name;
          rotationNeeds[rotationToUse.name] = Math.max(0, (rotationNeeds[rotationToUse.name] || 0) - 1);
          
          // Update tracking
          if (!globalBlockCoverage[rotationToUse.name]) {
            globalBlockCoverage[rotationToUse.name] = {};
          }
          if (!globalBlockCoverage[rotationToUse.name][blockIndex]) {
            globalBlockCoverage[rotationToUse.name][blockIndex] = [];
          }
          if (!globalBlockCoverage[rotationToUse.name][blockIndex].includes(residentId)) {
            globalBlockCoverage[rotationToUse.name][blockIndex].push(residentId);
            updateRotationBlockDistribution(rotationToUse.name, blockIndex);
          }
        }
      }
    });
  });

  Object.entries(residentRotationNeeds).forEach(([residentId, needs]) => {
    Object.entries(needs).forEach(([rotationName, weeksRemaining]) => {
      if (weeksRemaining > 0) {
        console.warn(`[Generate Block Schedule] Resident ${residentId}: Rotation ${rotationName} still needs ${weeksRemaining} weeks after scheduling`);
      }
    });
  });

  console.log('[Generate Block Schedule] Schedule generation complete');
  return newSchedule;
};
